import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import TableCell from "@material-ui/core/TableCell";
import { ConnectedPaginatedTable } from "components/table";
import assetsByGraphicSetQuery from "./query";
import { AssetDisplay } from "components/thumbnail";
import TableRow from "@material-ui/core/TableRow";
import { HeaderCell, TableIconCell } from "components/table";
import { FormattedMessage } from "react-intl";
import { injectIntl } from "react-intl";
import { changeAssetSpaceMutation } from "./asset-space-mutation";
import { useMutation } from "@apollo/react-hooks";
import FormConfirm from "components/form/form-confirm";
import IconButton from "components/icon-button";
import Icon from "components/icon";
import { useLoadingContext } from "components/loading-spinner-overlay";
import styles from "./styles/assets.table.css.js";
import withStyles from "components/with-styles";
import { eventStatusTypes } from "pages/event/utils";
import { useImperativeTracking } from "components/tracking";
import { Restricted, permissions } from "components/auth";

const Header = ({ onChangeOrdering, orderBy }) => (
  <TableRow>
    <HeaderCell />
    <HeaderCell onChangeOrdering={onChangeOrdering} orderBy={orderBy} orderName="orderByCodename">
      <FormattedMessage id="common.name" />
    </HeaderCell>
    <HeaderCell />
  </TableRow>
);

Header.propTypes = {
  /**
   ** Callback que se invoca al cambiar la columna actual
   **/
  onChangeOrdering: PropTypes.func,
  /**
   ** Ordenación de la columna actual
   **/
  orderBy: PropTypes.object,
};

const RowComponent = ({
  intl,
  item,
  eventId,
  availableSpaces,
  graphicSetId,
  customerId,
  setSuccess,
  setLoading,
  setShowConfirm,
  eventStatus,
}) => {
  const track = useImperativeTracking();
  const [changeAssetSpace] = useMutation(changeAssetSpaceMutation);

  const handleAssetChangeSpace = () => {
    track(`User changed asset space for event [${eventId}], graphicSet [${graphicSetId}], asset [${item.id}]`);
    setLoading(true);
    const variables = { eventId, graphicSetId, assetId: item.id };
    const updatedData = changeAssetSpace({ variables });

    return updatedData
      .then(result => {
        const asset = result.data.changeAssetSpace;
        setSuccess(Boolean(asset && asset.id));
      })
      .catch(() => {
        setSuccess(false);
      })
      .finally(() => {
        setShowConfirm(true);
        setLoading(false);
      });
  };

  return (
    <TableRow key={item.id}>
      <TableCell>
        <AssetDisplay withModal asset={item} width={50} />
      </TableCell>
      <TableCell>{item.codename}</TableCell>
      <TableIconCell>
        {eventStatus !== eventStatusTypes.FINISHED && (
          <Restricted permissions={[permissions.event_manage]}>
            <IconButton onClick={handleAssetChangeSpace}>
              <Icon name="send" />
            </IconButton>
          </Restricted>
        )}
      </TableIconCell>
    </TableRow>
  );
};

RowComponent.propTypes = {
  /**
   ** Elemento a representar en la fila de la tabla
   **/
  item: PropTypes.object,
  /**
   ** Id del evento
   **/
  eventId: PropTypes.string,
  /**
   ** Estado del evento
   **/
  eventStatus: PropTypes.string,
  /**
   ** Id del graphicset
   **/
  graphicSetId: PropTypes.string,
  /**
   ** Id del customer
   **/
  customerId: PropTypes.string,
  /**
   ** Espacios disponibles para mover el asset
   **/
  availableSpaces: PropTypes.array,
  /**
   ** Callback que se llama al tener éxito la llamada
   **/
  setSuccess: PropTypes.func,
  /**
   ** Callback que se llama al cargar la llamada
   **/
  setLoading: PropTypes.func,
  /**
   ** Callback que se llama al mostrar el diálogo de confirmación
   **/
  setShowConfirm: PropTypes.func,
};

const I18nStyledRowComponent = injectIntl(withStyles(styles)(RowComponent));

const AssetPaginatedTable = ({
  eventId,
  eventStatus,
  graphicSetId,
  customerId,
  intl,
  classes,
  availableSpaces,
  ...props
}) => {
  const { setLoading } = useLoadingContext();
  const [show, setShow] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleCloseConfirm = useCallback(() => setShow(false), []);

  return (
    <>
      <FormConfirm
        className={classes.confirm}
        show={show}
        error={!success}
        onClose={handleCloseConfirm}
        label={
          success
            ? intl.formatMessage({ id: "graphicset.asset.space.form.successful" })
            : intl.formatMessage({ id: "graphicset.asset.space.form.error" })
        }
      />
      <ConnectedPaginatedTable
        className={classes.table}
        debounce={false}
        headerComponent={Header}
        variables={{ graphicSetId, customerId, offset: undefined, limit: undefined }}
        gqlQuery={assetsByGraphicSetQuery}
        rowComponent={I18nStyledRowComponent}
        rowProps={{
          eventId,
          graphicSetId,
          customerId,
          availableSpaces,
          setSuccess,
          setLoading,
          eventStatus,
          setShowConfirm: setShow,
        }}
        paginatedElementsHandler={data => data.assetsByGraphicSet}
        showPagination={false}
        {...props}
      />
    </>
  );
};

AssetPaginatedTable.propTypes = {
  /**
   ** Estado del evento
   **/
  eventStatus: PropTypes.string,
  /**
   ** Página a cargar en la tabla
   **/
  page: PropTypes.number,
  /**
   ** Id del evento
   **/
  eventId: PropTypes.string,
  /**
   ** Id del graphicset al que pertenecen los assets
   **/
  graphicSetId: PropTypes.string,
  /**
   ** Id del customer del evento
   **/
  customerId: PropTypes.string,
  /**
   ** Espacios disponibles para mover el asset
   **/
  availableSpaces: PropTypes.array,
};

export default injectIntl(withStyles(styles)(AssetPaginatedTable));
